import React from 'react';
import { TextPairing } from '@components/TextPairing';
import { Flex } from '@components/Flex';
import { Box } from '@components/Box';
import { CTALink } from '@components/CTALink';
import { Column, Columns } from '@components/Columns';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { AvatarSizes } from '@components/Avatar';
import {
  AllTogetherTableContainer,
  AnimationInnerContainer,
  AnimationOuterContainer,
  RowsContainer,
  StyledAllTogetherLastRowWrapper,
  StyledAllTogetherTableRow,
  StyledShadow,
  StyledTable,
  TableAvatar,
  TableDecorLeftImage,
  TableDecorRightImage,
  TableWrapper,
} from './AllTogetherNowSection.styles';
import { Lottie, PartyLottie } from './AllTogetherSectionMobile.styles';
import { AllTogetherTableHeader, TextCell, getCheckMarkImage } from './AllTogetherNowSection';
import { AllTogetherSectionMobileController } from './AllTogetherSectionMobile.controller';
import { useTranslation } from 'react-i18next';

export const AllTogetherSectionMobile: React.FC<{}> = () => {
  const { t } = useTranslation('onlineRsvp');
  const rows = t('allTogetherTable.rows');
  const { unified, households, rsvpGroups } = t('allTogetherNowMobile');

  const { tableRef, partiesRef, initBodyMovinAnimation } = AllTogetherSectionMobileController();

  return (
    <Box className="all-together-section-mobile" as="section" paddingTop={0} paddingBottom={[8, 15, 16, 16, 13]} backgroundColor="yellow1">
      <LazyLoadWrapper beforeLoad={initBodyMovinAnimation}>
        <div></div>
      </LazyLoadWrapper>
      <Columns>
        <Column width={12}>
          <Flex flexDirection="column" alignItems="center">
            <TextPairing
              marginBottom={8}
              alignment="center"
              eyebrowColor="blue5"
              eyebrowVariant="calloutHed32"
              eyebrow={unified.eyebrow}
              eyebrowTagName="h2"
              titleColor="blue6"
              titleVariant="hed120Super"
              title={unified.title}
              titleTagName="h2"
              bodyColor="blue6"
              bodyVariant="dek24"
              body={unified.content}
              bodyMaxWidth="805px"
            />
            <CTALink id="allTogetherNowToGuestListMobile" href="/guest-list" typography="featureHed20" hasChevron>
              Explore the Connected Guest List
            </CTALink>
          </Flex>
        </Column>
        <Column width={12}>
          <Flex id="together-table-lottie" flexDirection="column" alignItems="center">
            <AllTogetherTableContainer flexDirection="column">
              <TableWrapper>
                <TableDecorLeftImage src="wedding-invitations/unified-rsvp/left.png" />
                <StyledTable>
                  <AllTogetherTableHeader />
                  <RowsContainer>
                    {rows.map((row, index) => {
                      const isLastRow = index === rows.length - 1;
                      const rowUi = (
                        <StyledAllTogetherTableRow key={index} style={{ zIndex: index === 3 ? 1 : 'unset' }}>
                          <TableAvatar
                            sizeVariant={AvatarSizes.sm}
                            strokeWidth="3px"
                            strokeColor="blue3"
                            isHidden={index === 3 || index === 4}
                            src={row.src}
                          />
                          {index === 3 && (
                            <AnimationOuterContainer>
                              <AnimationInnerContainer>
                                <Lottie ref={tableRef}></Lottie>
                              </AnimationInnerContainer>
                            </AnimationOuterContainer>
                          )}
                          <TextCell style={{ flex: 1.4 }}>{row.name}</TextCell>
                          <div style={{ flex: 1 }}>{getCheckMarkImage(row.rehearsal)}</div>
                          <div style={{ flex: 1 }}>{getCheckMarkImage(row.ceremony)}</div>
                          <div style={{ flex: 1 }}>{getCheckMarkImage(row.reception)}</div>
                          <div style={{ flex: 1 }}>{getCheckMarkImage(row.brunch)}</div>
                        </StyledAllTogetherTableRow>
                      );
                      return isLastRow ? <StyledAllTogetherLastRowWrapper key={index}>{rowUi}</StyledAllTogetherLastRowWrapper> : rowUi;
                    })}
                  </RowsContainer>
                </StyledTable>
                <StyledShadow />
                <TableDecorRightImage src="wedding-invitations/unified-rsvp/right.png" isDecor />
              </TableWrapper>
            </AllTogetherTableContainer>
          </Flex>
        </Column>
        <Column width={12}>
          <TextPairing
            id="household-party-lottie"
            eyebrowColor="blue5"
            eyebrowVariant="sectionEyeBrow24"
            eyebrow={households.eyebrow}
            titleColor="blue6"
            titleVariant="hed72Hero"
            title={households.title}
            titleTagName="h3"
            bodyColor="blue6"
            bodyVariant="dek24"
            body={households.content}
            titleMaxWidth="330px"
            bodyMaxWidth="438px"
          />
        </Column>
        <Column width={12}>
          <PartyLottie ref={partiesRef}></PartyLottie>
        </Column>
        <Column width={12}>
          <TextPairing
            eyebrowTagName="h2"
            eyebrowColor="blue5"
            eyebrowVariant="sectionEyeBrow24"
            eyebrow={rsvpGroups.eyebrow}
            titleColor="blue6"
            titleVariant="hed56"
            title={rsvpGroups.title}
            titleTagName="h3"
            bodyColor="blue6"
            bodyVariant="dek20"
            body={rsvpGroups.content}
            bodyMaxWidth="338px"
          />
        </Column>
      </Columns>
    </Box>
  );
};

export default AllTogetherSectionMobile;
